import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  adminXeroAccountsAction,
  adminXeroConnectAction,
  adminXeroDisconnectAction,
  adminXeroSettingsGetAction,
  adminXeroSettingsUpdateAction,
  adminXeroStatusAction,
  adminXeroSyncGetAction,
  adminXeroSyncRetryAction,
  adminXeroTaxRatesAction
} from "../Action/action";

const OAUTH_POLL_MS = 2500;
const OAUTH_POLL_MAX_MS = 5 * 60 * 1000;

function toBool01(v, fallback = false) {
  if (v === undefined || v === null || v === "") return fallback;
  if (typeof v === "boolean") return v;
  const s = String(v).trim().toLowerCase();
  return s === "1" || s === "true" || s === "yes" || s === "on";
}

function statusBadge(ok) {
  return ok ? "badge-success" : "badge-warning";
}

function connectionFingerprint(data) {
  if (!data) return "";
  return [data.connected ? "1" : "0", data.tenant_id || "", data.token_expires_at || ""].join("|");
}

export default function XeroSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [awaitingOauth, setAwaitingOauth] = useState(false);
  const [error, setError] = useState("");
  const oauthPollRef = useRef(null);
  const oauthBaselineRef = useRef("");
  const oauthStartedAtRef = useRef(0);
  const awaitingOauthRef = useRef(false);

  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [salesCandidates, setSalesCandidates] = useState([]);
  const [paymentCandidates, setPaymentCandidates] = useState([]);
  const [taxRates, setTaxRates] = useState([]);

  const [salesAccountCode, setSalesAccountCode] = useState("");
  const [paymentAccountCode, setPaymentAccountCode] = useState("");
  const [taxType, setTaxType] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("AUTHORISED");
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [includeStripe, setIncludeStripe] = useState(true);
  const [includeAppleIap, setIncludeAppleIap] = useState(true);
  const [includeAdminGift, setIncludeAdminGift] = useState(true);
  const [skipDemoUsers, setSkipDemoUsers] = useState(false);

  const [purchaseId, setPurchaseId] = useState("");
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncRow, setSyncRow] = useState(null);

  const applySettings = useCallback((map) => {
    const s = map || {};
    setSalesAccountCode(s.sales_account_code || "");
    setPaymentAccountCode(s.payment_account_code || "");
    setTaxType(s.tax_type || "");
    setInvoiceStatus(s.invoice_status || "AUTHORISED");
    setSyncEnabled(toBool01(s.sync_enabled, true));
    setIncludeStripe(toBool01(s.include_stripe, true));
    setIncludeAppleIap(toBool01(s.include_apple_iap, true));
    setIncludeAdminGift(toBool01(s.include_admin_gift, true));
    setSkipDemoUsers(toBool01(s.skip_demo_users, false));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [statusRes, settingsRes] = await Promise.all([
        adminXeroStatusAction(),
        adminXeroSettingsGetAction()
      ]);
      if (!statusRes?.success) {
        throw new Error(statusRes?.message || "Failed to load Xero status");
      }
      setStatus(statusRes.data || null);
      if (settingsRes?.success) applySettings(settingsRes.data);

      if (statusRes?.data?.connected) {
        const [accRes, taxRes] = await Promise.all([
          adminXeroAccountsAction(),
          adminXeroTaxRatesAction()
        ]);
        if (accRes?.success) {
          setAccounts(accRes.data?.accounts || []);
          setSalesCandidates(accRes.data?.sales_candidates || []);
          setPaymentCandidates(accRes.data?.payment_candidates || []);
        } else {
          setAccounts([]);
          setSalesCandidates([]);
          setPaymentCandidates([]);
        }
        if (taxRes?.success) {
          setTaxRates(taxRes.data?.tax_rates || []);
        } else {
          setTaxRates([]);
        }
      } else {
        setAccounts([]);
        setSalesCandidates([]);
        setPaymentCandidates([]);
        setTaxRates([]);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load Xero");
    } finally {
      setLoading(false);
    }
  }, [applySettings]);

  const stopOauthWatch = useCallback(() => {
    if (oauthPollRef.current) {
      clearInterval(oauthPollRef.current);
      oauthPollRef.current = null;
    }
    awaitingOauthRef.current = false;
    setAwaitingOauth(false);
    oauthBaselineRef.current = "";
    oauthStartedAtRef.current = 0;
  }, []);

  const finishOauthRefresh = useCallback(
    async ({ ok, message } = {}) => {
      stopOauthWatch();
      await loadAll();
      if (ok === false) {
        toast.error(message || "Xero connection failed", { toastId: "xero-oauth" });
      } else {
        toast.success(message || "Xero connection updated", { toastId: "xero-oauth" });
      }
    },
    [loadAll, stopOauthWatch]
  );

  const startOauthWatch = useCallback(
    (baselineFingerprint) => {
      if (oauthPollRef.current) {
        clearInterval(oauthPollRef.current);
        oauthPollRef.current = null;
      }
      oauthBaselineRef.current = baselineFingerprint || "";
      oauthStartedAtRef.current = Date.now();
      awaitingOauthRef.current = true;
      setAwaitingOauth(true);

      oauthPollRef.current = setInterval(async () => {
        if (Date.now() - oauthStartedAtRef.current > OAUTH_POLL_MAX_MS) {
          stopOauthWatch();
          toast.info("Still waiting on Xero? Use Refresh status if the popup already finished.", {
            toastId: "xero-oauth-timeout"
          });
          return;
        }
        try {
          const res = await adminXeroStatusAction();
          if (!res?.success) return;
          const nextFp = connectionFingerprint(res.data);
          if (nextFp && nextFp !== oauthBaselineRef.current) {
            await finishOauthRefresh({
              ok: Boolean(res.data?.connected),
              message: res.data?.connected
                ? `Connected to ${res.data.tenant_name || res.data.tenant_id || "Xero"}`
                : "Xero connection status changed"
            });
          }
        } catch (_) {
          /* keep polling */
        }
      }, OAUTH_POLL_MS);
    },
    [finishOauthRefresh, stopOauthWatch]
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // OAuth popup → postMessage + focus check (client-side only; no cron).
  useEffect(() => {
    const onMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== "xero-oauth") return;
      if (data.ok) {
        finishOauthRefresh({
          ok: true,
          message: data.tenant_name
            ? `Connected to ${data.tenant_name}`
            : "Xero connected"
        });
      } else {
        finishOauthRefresh({
          ok: false,
          message: data.error || "Xero connection failed"
        });
      }
    };

    const onFocusOrVisible = () => {
      if (!awaitingOauthRef.current) return;
      adminXeroStatusAction()
        .then((res) => {
          if (!res?.success) return;
          const nextFp = connectionFingerprint(res.data);
          if (nextFp && nextFp !== oauthBaselineRef.current) {
            return finishOauthRefresh({
              ok: Boolean(res.data?.connected),
              message: res.data?.connected
                ? `Connected to ${res.data.tenant_name || res.data.tenant_id || "Xero"}`
                : "Xero connection status changed"
            });
          }
          return null;
        })
        .catch(() => {});
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
  }, [finishOauthRefresh]);

  useEffect(() => {
    return () => {
      if (oauthPollRef.current) {
        clearInterval(oauthPollRef.current);
        oauthPollRef.current = null;
      }
    };
  }, []);

  const salesOptions = useMemo(() => {
    const list = salesCandidates.length ? salesCandidates : accounts;
    return list || [];
  }, [salesCandidates, accounts]);

  const paymentOptions = useMemo(() => {
    const list = paymentCandidates.length ? paymentCandidates : accounts;
    return list || [];
  }, [paymentCandidates, accounts]);

  const onConnect = async () => {
    setConnecting(true);
    try {
      const res = await adminXeroConnectAction();
      if (!res?.success || !res?.data?.authorize_url) {
        throw new Error(res?.message || "Could not get authorize URL");
      }
      const baseline = connectionFingerprint(status);
      // Keep window.opener so the callback page can postMessage back (do not use noopener).
      const popup = window.open(
        res.data.authorize_url,
        "klc_xero_oauth",
        "width=980,height=720,menubar=no,toolbar=no,status=no"
      );
      startOauthWatch(baseline);
      if (!popup) {
        toast.warning("Popup blocked — allow popups, or open the authorize URL manually; this page will still auto-refresh.", {
          toastId: "xero-connect"
        });
      } else {
        toast.info("Complete Xero login in the popup. This page refreshes automatically when done.", {
          toastId: "xero-connect"
        });
      }
    } catch (e) {
      stopOauthWatch();
      toast.error(e?.response?.data?.message || e?.message || "Connect failed", {
        toastId: "xero-connect"
      });
    } finally {
      setConnecting(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect the active Xero organisation?")) return;
    setDisconnecting(true);
    stopOauthWatch();
    try {
      const res = await adminXeroDisconnectAction();
      if (!res?.success) throw new Error(res?.message || "Disconnect failed");
      toast.success("Xero disconnected", { toastId: "xero-disconnect" });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Disconnect failed", {
        toastId: "xero-disconnect"
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const res = await adminXeroSettingsUpdateAction({
        sales_account_code: salesAccountCode || null,
        payment_account_code: paymentAccountCode || null,
        tax_type: taxType || null,
        invoice_status: invoiceStatus || "AUTHORISED",
        sync_enabled: syncEnabled ? "1" : "0",
        include_stripe: includeStripe ? "1" : "0",
        include_apple_iap: includeAppleIap ? "1" : "0",
        include_admin_gift: includeAdminGift ? "1" : "0",
        skip_demo_users: skipDemoUsers ? "1" : "0"
      });
      if (!res?.success) throw new Error(res?.message || "Save failed");
      applySettings(res.data);
      toast.success("Xero settings saved", { toastId: "xero-save" });
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", {
        toastId: "xero-save"
      });
    } finally {
      setSaving(false);
    }
  };

  const onLookupSync = async () => {
    const id = Number(purchaseId);
    if (!Number.isInteger(id) || id < 1) {
      toast.error("Enter a valid purchase id", { toastId: "xero-sync" });
      return;
    }
    setSyncBusy(true);
    setSyncRow(null);
    try {
      const res = await adminXeroSyncGetAction(id);
      if (!res?.success) throw new Error(res?.message || "No sync row");
      setSyncRow(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Lookup failed", {
        toastId: "xero-sync"
      });
    } finally {
      setSyncBusy(false);
    }
  };

  const onRetrySync = async () => {
    const id = Number(purchaseId);
    if (!Number.isInteger(id) || id < 1) {
      toast.error("Enter a valid purchase id", { toastId: "xero-sync" });
      return;
    }
    setSyncBusy(true);
    try {
      const res = await adminXeroSyncRetryAction(id);
      if (!res?.success) {
        toast.error(res?.message || "Sync failed", { toastId: "xero-sync" });
      } else {
        toast.success(res?.message || "Sync processed", { toastId: "xero-sync" });
      }
      setSyncRow(res?.data?.sync || null);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Retry failed", {
        toastId: "xero-sync"
      });
    } finally {
      setSyncBusy(false);
    }
  };

  const connected = Boolean(status?.connected);
  const ready = Boolean(status?.ready_to_sync);

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Xero unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card mb-6">
        <div className="settings-section">
          <div className="flex items-center gap-3" style={{ justifyContent: "space-between", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ marginBottom: 4 }}>Connection</h3>
              <p className="small-text" style={{ margin: 0 }}>
                Connect once per environment. Status refreshes automatically after Connect / Disconnect.
              </p>
              {awaitingOauth ? (
                <p className="small-text" style={{ marginTop: 8, color: "var(--primary, #2B7BCA)" }}>
                  Waiting for Xero login to finish…
                </p>
              ) : null}
            </div>
            <div className="flex gap-3" style={{ flexWrap: "wrap" }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={loadAll} disabled={loading}>
                Refresh status
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={onConnect}
                disabled={connecting || loading || !status?.configured}
              >
                {connecting ? "Opening…" : connected ? "Reconnect" : "Connect to Xero"}
              </button>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={onDisconnect}
                disabled={!connected || disconnecting || loading}
              >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
              </button>
            </div>
          </div>

          {loading && !status ? (
            <p className="small-text" style={{ marginTop: 16 }}>
              Loading…
            </p>
          ) : (
            <div className="form-row" style={{ marginTop: 16 }}>
              <div className="form-group">
                <label>Configured</label>
                <div>
                  <span className={`badge ${statusBadge(status?.configured)}`}>
                    {status?.configured ? "Yes" : "No"}
                  </span>
                  {status?.config_error ? (
                    <p className="small-text" style={{ marginTop: 6 }}>
                      {status.config_error}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="form-group">
                <label>Connected</label>
                <div>
                  <span className={`badge ${statusBadge(connected)}`}>{connected ? "Yes" : "No"}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Ready to sync</label>
                <div>
                  <span className={`badge ${statusBadge(ready)}`}>{ready ? "Yes" : "No"}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Auth mode</label>
                <input className="form-control" value={status?.auth_mode || "—"} readOnly />
              </div>
              <div className="form-group">
                <label>Organisation</label>
                <input
                  className="form-control"
                  value={status?.tenant_name || status?.tenant_id || "—"}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Redirect URI</label>
                <input className="form-control" value={status?.redirect_uri || "—"} readOnly />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card mb-6">
        <div className="settings-section">
          <h3>Invoice accounts &amp; tax</h3>
          <p className="small-text" style={{ marginBottom: 16 }}>
            Values come from the connected Xero organisation. Save before testing purchase sync.
          </p>

          <div className="form-row">
            <div className="form-group">
              <label>Sales account code</label>
              <select
                className="form-control"
                value={salesAccountCode}
                onChange={(e) => setSalesAccountCode(e.target.value)}
                disabled={!connected || loading}
              >
                <option value="">Select…</option>
                {salesOptions.map((a) => (
                  <option key={`s-${a.code}`} value={a.code}>
                    {a.code} — {a.name || a.type || ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Payment account code</label>
              <select
                className="form-control"
                value={paymentAccountCode}
                onChange={(e) => setPaymentAccountCode(e.target.value)}
                disabled={!connected || loading}
              >
                <option value="">Select…</option>
                {paymentOptions.map((a) => (
                  <option key={`p-${a.code}`} value={a.code}>
                    {a.code} — {a.name || a.type || ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Tax type</label>
              <select
                className="form-control"
                value={taxType}
                onChange={(e) => setTaxType(e.target.value)}
                disabled={!connected || loading}
              >
                <option value="">Select…</option>
                {taxRates.map((t) => (
                  <option key={t.tax_type} value={t.tax_type}>
                    {t.tax_type}
                    {t.name ? ` — ${t.name}` : ""}
                    {t.effective_rate != null ? ` (${t.effective_rate}%)` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Invoice status</label>
              <select
                className="form-control"
                value={invoiceStatus}
                onChange={(e) => setInvoiceStatus(e.target.value)}
                disabled={loading}
              >
                <option value="AUTHORISED">AUTHORISED</option>
                <option value="DRAFT">DRAFT</option>
                <option value="SUBMITTED">SUBMITTED</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="settings-section">
          <h3>Sync rules</h3>
          <p className="small-text" style={{ marginBottom: 16 }}>
            Which purchase types create Xero invoices. Admin gifts sync at $0 (invoice only, no payment).
          </p>

          <div className="toggle-row">
            <div className="toggle-label">
              <h4>Sync enabled</h4>
              <p>Master switch for enqueue / process</p>
            </div>
            <button
              type="button"
              className={`toggle-switch ${syncEnabled ? "on" : ""}`}
              onClick={() => setSyncEnabled((v) => !v)}
              aria-label="Sync enabled"
              style={{ border: "none" }}
            />
          </div>
          <div className="toggle-row">
            <div className="toggle-label">
              <h4>Include Stripe / Android web</h4>
              <p>payment_provider stripe, android_web</p>
            </div>
            <button
              type="button"
              className={`toggle-switch ${includeStripe ? "on" : ""}`}
              onClick={() => setIncludeStripe((v) => !v)}
              aria-label="Include Stripe"
              style={{ border: "none" }}
            />
          </div>
          <div className="toggle-row">
            <div className="toggle-label">
              <h4>Include Apple IAP</h4>
              <p>payment_provider apple_iap</p>
            </div>
            <button
              type="button"
              className={`toggle-switch ${includeAppleIap ? "on" : ""}`}
              onClick={() => setIncludeAppleIap((v) => !v)}
              aria-label="Include Apple IAP"
              style={{ border: "none" }}
            />
          </div>
          <div className="toggle-row">
            <div className="toggle-label">
              <h4>Include admin gifts</h4>
              <p>payment_provider admin_gift (for testing / free grants)</p>
            </div>
            <button
              type="button"
              className={`toggle-switch ${includeAdminGift ? "on" : ""}`}
              onClick={() => setIncludeAdminGift((v) => !v)}
              aria-label="Include admin gifts"
              style={{ border: "none" }}
            />
          </div>
          <div className="toggle-row">
            <div className="toggle-label">
              <h4>Skip demo users</h4>
              <p>When on, demo users are not synced</p>
            </div>
            <button
              type="button"
              className={`toggle-switch ${skipDemoUsers ? "on" : ""}`}
              onClick={() => setSkipDemoUsers((v) => !v)}
              aria-label="Skip demo users"
              style={{ border: "none" }}
            />
          </div>

          <div style={{ marginTop: 20 }}>
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || loading}>
              {saving ? "Saving…" : "Save Xero settings"}
            </button>
          </div>
        </div>
      </div>

      <div className="card mb-6 d-none">
        <div className="settings-section">
          <h3>Purchase sync</h3>
          <p className="small-text" style={{ marginBottom: 16 }}>
            Look up or retry Xero sync for a purchase id (e.g. an admin gift).
          </p>
          <div className="form-row" style={{ alignItems: "flex-end" }}>
            <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
              <label>Purchase ID</label>
              <input
                type="number"
                className="form-control"
                min={1}
                value={purchaseId}
                onChange={(e) => setPurchaseId(e.target.value)}
                placeholder="e.g. 42"
              />
            </div>
            <div className="flex gap-3" style={{ marginBottom: 16, flexWrap: "wrap" }}>
              <button type="button" className="btn btn-outline" onClick={onLookupSync} disabled={syncBusy}>
                Look up
              </button>
              <button type="button" className="btn btn-primary" onClick={onRetrySync} disabled={syncBusy}>
                {syncBusy ? "Working…" : "Enqueue & retry"}
              </button>
            </div>
          </div>
          {syncRow ? (
            <pre
              style={{
                marginTop: 8,
                padding: 12,
                background: "var(--gray-100, #f5f5f5)",
                borderRadius: 8,
                overflow: "auto",
                fontSize: 12
              }}
            >
              {JSON.stringify(syncRow, null, 2)}
            </pre>
          ) : null}
        </div>
      </div>
    </div>
  );
}
